import { createReducer } from 'typesafe-actions';
import { formatPrice } from '@westwing/appshell-formatting-helpers';
import { get } from '../../../common/utils/objectUtils';
import { defaultRequestHandler, defaultErrorHandler } from '../dataFetching/generators';
import { PriceFormatConfigOptions } from '../products/types';
import { ProductImageTag } from '../productdetail/types';
import { CartActionType, fetchCartCountAction, fetchCartDataAction, addToCartAction } from './actions';
import { CartState, CartProductItem, CLEAR_CART, MARK_FETCHED, CLEAR_CART_DATA } from './types';

export const defaultState: CartState = {
    isFetching: false,
    didInvalidate: false,
    identifier: '',
    count: 0,
    isFetched: false,
    isCartDataFetched: false,
    carts: [],
    addToCartState: {
        identifier: '',
        didInvalidate: false,
        isFetching: false,
    },
};
const defaultPriceFormatConfig = { countryCode: '', currency: '' };

const mapResponseToCart = (countryCode: string, currency: string) => (cart: CartProductItem): CartProductItem => ({
    name: cart.name,
    brand: cart.brand,
    description: cart.description,
    image: cart.image,
    imagePath: cart?.product_image?.path || '',
    imageTag: cart?.product_image?.tag || ProductImageTag.REGULAR,
    originalPrice: formatPrice(countryCode, currency, Number(cart.originalPrice)),
    price: formatPrice(countryCode, currency, Number(cart.price)),
    quantity: cart.quantity,
    sku: cart.sku,
    stock: cart.stock,
});

export default createReducer<CartState, CartActionType>(defaultState)
    .handleAction(addToCartAction.request, (state, action) => {
        return {
            ...state,
            addToCartState: defaultRequestHandler(state.addToCartState, action),
        };
    })
    .handleAction(addToCartAction.success, state => {
        return {
            ...state,
            addToCartState: {
                ...state.addToCartState,
                isFetching: false,
                didInvalidate: false,
                isFetchingComplete: true,
            },
        };
    })
    .handleAction(addToCartAction.failure, (state, action) => {
        return {
            ...state,
            addToCartState: defaultErrorHandler(state.addToCartState, action),
        };
    })
    .handleAction(fetchCartDataAction.success, (state, action) => {
        const { reducerPayloadOptions } = action.payload;
        const { countryCode, currency } = <PriceFormatConfigOptions>reducerPayloadOptions || defaultPriceFormatConfig;
        const items = get(action.payload.data, 'data.items', []);
        const cartitems = Array.isArray(items) ? items.map(mapResponseToCart(countryCode, currency)) : [];
        return {
            ...state,
            isFetching: false,
            isCartDataFetched: true,
            carts: cartitems,
            subtotal: formatPrice(countryCode, currency, get(action.payload.data, 'data.subtotal', 0)),
        };
    })
    .handleAction(fetchCartDataAction.failure, (state, action) => defaultErrorHandler(state, action))
    .handleAction(fetchCartDataAction.request, (state, action) => defaultRequestHandler(state, action))
    .handleAction(CLEAR_CART, () => {
        return { ...defaultState };
    })
    .handleAction(CLEAR_CART_DATA, state => {
        return { ...state, isFetching: false, isCartDataFetched: false, carts: [], subtotal: '0' };
    })
    .handleAction(MARK_FETCHED, () => {
        return { ...defaultState, isFetched: true };
    })
    // Handle cart count actions
    .handleAction(fetchCartCountAction.success, (state, action) => {
        return {
            ...state,
            isFetching: false,
            isFetched: true,
            didInvalidate: false,
            count: get(action.payload.data, 'data.count', 0),
        };
    })
    .handleAction(fetchCartCountAction.failure, (state, action) => defaultErrorHandler(state, action))
    .handleAction(fetchCartCountAction.request, (state, action) => defaultRequestHandler(state, action));
